import { useState, useCallback, useMemo, useRef } from "react";
import { SlideoutDrawerV2, Flex, Tabs, Tab, OverlayStyle, toast } from "@jsq/ds/core";
import { useFeatureFlag } from "@jsq/flags-react";
import { useAssignReviewerMutation } from "~/graphql/supergraphHooks";
import { CaseDetailsFooter } from "./CaseDetailsFooter";
import { CaseDetailsHeader } from "./CaseDetailsHeader";
import { EntitiesTab } from "./EntitiesTab";
import { LogTab } from "./LogTab";
import * as styles from "./styles_vanilla.css";
import { CaseDetailsTabType } from "./types";
import { buildSubmittedEumCase } from "./utils/buildSubmittedEumCase";
import { getPendingChangeRequestIdsByType } from "./utils/getPendingChangeRequestIdsByType";
import type { BillingCaseRowItem, CaseStatus } from "../types";
import type { EumApproverPickerSubmitArgs } from "./EumApproverPickerModal/types";
import type { CaseDetailsDrawerProps, EntitiesTabActions, ReviewerConclusion } from "./types";

export const CaseDetailsDrawer = ({
  billingCase,
  onClose,
  isChecker = false,
  pendingChangeRequestIds = [],
}: CaseDetailsDrawerProps) => {
  const [activeTab, setActiveTab] = useState<CaseDetailsTabType>(CaseDetailsTabType.Entities);
  const [reviewerConclusion, setReviewerConclusion] = useState<ReviewerConclusion | null>(null);
  const [overriddenStatus, setOverriddenStatus] = useState<CaseStatus | null>(null);
  const [createdCase, setCreatedCase] = useState<BillingCaseRowItem | null>(null);
  const [hasLocalChanges, setHasLocalChanges] = useState(false);
  const [hasPendingEumChanges, setHasPendingEumChanges] = useState(false);
  const entitiesActionsRef = useRef<EntitiesTabActions | null>(null);

  const isLogTabEnabled = useFeatureFlag("billingFirstCapitalCallDate", false);

  const [assignReviewer] = useAssignReviewerMutation({
    onError: (error) => {
      toast.error(error.message);
    },
  });

  const handleEntitiesActionsReady = useCallback((actions: EntitiesTabActions) => {
    entitiesActionsRef.current = actions;
  }, []);

  const { legacyPendingIds, gpPendingIds } = useMemo(
    () => getPendingChangeRequestIdsByType(billingCase?.requestType, pendingChangeRequestIds),
    [billingCase?.requestType, pendingChangeRequestIds],
  );

  const handleLocalChangesUpdate = useCallback(
    (hasChanges: boolean, hasEumChanges: boolean) => {
      setHasLocalChanges(hasChanges);
      setHasPendingEumChanges(hasEumChanges);
    },
    [],
  );

  const handleSubmitEumForReview = useCallback(
    async ({ billingApproverId, csmManagerApproverId }: EumApproverPickerSubmitArgs) => {
      if (!entitiesActionsRef.current || !billingCase) {
        return;
      }

      const results = await entitiesActionsRef.current.submitAllPendingEumChanges({
        billingApproverId,
        csmManagerApproverId,
      });

      const submittedCase = buildSubmittedEumCase(billingCase, results);
      if (!submittedCase) {
        toast.error("Submission failed. Please try again.");
        return;
      }

      setCreatedCase(submittedCase);
      setOverriddenStatus(submittedCase.status);
      toast.success("Submitted for approval");
    },
    [billingCase],
  );

  const handleSubmitForReview = useCallback(
    async (reviewerId: number) => {
      if (!entitiesActionsRef.current || !billingCase) {
        return;
      }

      const results = await entitiesActionsRef.current.createAllPendingChanges();

      if (results.length === 0) {
        if (!billingCase.changeRequestId) {
          return;
        }

        const result = await assignReviewer({
          variables: { changeRequestId: billingCase.changeRequestId, reviewerId },
        });

        const changeRequest = result.data?.assignReviewer?.changeRequest;
        if (changeRequest?.status) {
          setOverriddenStatus(changeRequest.status);
          toast.success("Submitted for review");
        }
        return;
      }

      const assignResults = await Promise.allSettled(
        results.map((cr) =>
          assignReviewer({
            variables: { changeRequestId: cr.changeRequestId, reviewerId },
          }),
        ),
      );

      let lastStatus: CaseStatus | null = null;
      for (const outcome of assignResults) {
        if (outcome.status !== "fulfilled") {
          continue;
        }
        const updatedStatus = outcome.value.data?.assignReviewer?.changeRequest?.status;
        if (updatedStatus) {
          lastStatus = updatedStatus;
        }
      }

      setCreatedCase({
        ...billingCase,
        changeRequestId: results[0].changeRequestId,
        billingCaseId: results[0].billingCaseId,
        status: lastStatus ?? results[0].status,
      });

      if (lastStatus) {
        setOverriddenStatus(lastStatus);
        toast.success("Submitted for review");
      }
    },
    [billingCase, assignReviewer],
  );

  const handleClose = useCallback(() => {
    setCreatedCase(null);
    setOverriddenStatus(null);
    setReviewerConclusion(null);
    setHasLocalChanges(false);
    setHasPendingEumChanges(false);
    onClose();
  }, [onClose]);

  if (!billingCase) {
    return null;
  }

  const activeCase = createdCase ?? billingCase;
  const isDraft = !createdCase && billingCase.changeRequestId === "";
  const displayedCase = overriddenStatus
    ? { ...activeCase, status: overriddenStatus }
    : activeCase;

  return (
    <SlideoutDrawerV2
      isOpen={!!billingCase}
      onClose={handleClose}
      isDismissable
      overlayStyle={OverlayStyle.Transparent}
      data-testid="case-details-drawer"
    >
      <Flex alignItems="flex-start" justifyContent="center" className={styles.drawerBody}>
        <Flex flexDirection="column" className={styles.drawerContent}>
          <div className={styles.headerSection}>
            <CaseDetailsHeader
              billingCase={displayedCase}
              onClose={handleClose}
              reviewerConclusion={reviewerConclusion}
              isDraft={isDraft}
              hasLocalChanges={hasLocalChanges}
              hasPendingCases={pendingChangeRequestIds.length > 0}
            />
          </div>
          <div className={styles.tabsContainer}>
            <Tabs hasPaddingInline={false}>
              <Tab
                active={activeTab === CaseDetailsTabType.Entities}
                text="Entities"
                onClick={() => setActiveTab(CaseDetailsTabType.Entities)}
                data-testid="case-details-entities-tab"
              />
              {isLogTabEnabled && (
                <Tab
                  active={activeTab === CaseDetailsTabType.Log}
                  text="Log"
                  onClick={() => setActiveTab(CaseDetailsTabType.Log)}
                  data-testid="case-details-log-tab"
                />
              )}
            </Tabs>
          </div>
          <div className={styles.tabContent}>
            {activeTab === CaseDetailsTabType.Entities && (
              <EntitiesTab
                isChecker={isChecker}
                clientId={activeCase.clientId}
                onLocalChangesUpdate={handleLocalChangesUpdate}
                onEntitiesActionsReady={handleEntitiesActionsReady}
                pendingChangeRequestIds={legacyPendingIds}
                pendingGpChangeRequestIds={gpPendingIds}
              />
            )}
            {isLogTabEnabled && activeTab === CaseDetailsTabType.Log && (
              <LogTab
                familyId={activeCase.familyId}
                billingCaseId={activeCase.billingCaseId}
                changeRequestId={activeCase.changeRequestId}
              />
            )}
          </div>
          <CaseDetailsFooter
            billingCase={displayedCase}
            onConclusionSubmit={setReviewerConclusion}
            onStatusChange={setOverriddenStatus}
            isChecker={isChecker}
            isDraft={isDraft}
            hasLocalChanges={hasLocalChanges}
            hasPendingEumChanges={hasPendingEumChanges}
            onSubmitForReview={handleSubmitForReview}
            onSubmitEumForReview={handleSubmitEumForReview}
          />
        </Flex>
      </Flex>
    </SlideoutDrawerV2>
  );
};
